// ========================================
// AI CHAT SERVICE
// ========================================

/**
 * Service for communicating with the AI Chat Assistant backend.
 * Handles sending messages and receiving responses.
 */

import config from '../config';
import { authenticatedFetch } from './authService';

/**
 * Send a chat message to the AI assistant
 *
 * @param {Array} messages - Array of chat messages { role: 'user'|'assistant', content: string }
 * @param {string} sessionId - Optional session identifier for conversation tracking
 * @param {Array} visibleTeamIds - Array of team IDs the user has selected in Team Management
 * @param {string} language - Language code for the response ('en' or 'th')
 * @returns {Promise<Object>} Response containing the AI's reply
 */
export const sendChatMessage = async (messages, sessionId = null, visibleTeamIds = [], language = 'en') => {
    const response = await authenticatedFetch(`${config.backendApiUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            messages,
            sessionId,
            visibleTeamIds,
            language
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Chat request failed: ${response.status}`);
    }

    return response.json();
};

/**
 * Check the status of the chat service
 *
 * @returns {Promise<Object>} Status information including configuration state
 */
export const getChatStatus = async () => {
    const response = await authenticatedFetch(`${config.backendApiUrl}/chat/status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        throw new Error(`Status check failed: ${response.status}`);
    }

    return response.json();
};

/**
 * Generate a unique session ID for chat conversations
 *
 * @returns {string} UUID for the session
 */
export const generateSessionId = () => {
    return crypto.randomUUID();
};

// ========================================
// FEEDBACK SERVICE
// ========================================

/**
 * Submit feedback for an AI chat response
 *
 * @param {Object} feedbackData - Feedback data
 * @param {string} feedbackData.sessionId - Chat session ID
 * @param {string} feedbackData.rating - 'positive' or 'negative'
 * @param {string} feedbackData.question - The user's question
 * @param {string} feedbackData.response - The AI's response
 * @param {string} feedbackData.comment - Optional user comment
 * @returns {Promise<Object>} Response from the server
 */
export const submitFeedback = async (feedbackData) => {
    const response = await authenticatedFetch(`${config.backendApiUrl}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Feedback submission failed: ${response.status}`);
    }

    return response.json();
};

/**
 * Get feedback logs (admin only)
 *
 * @param {Object} options - Query options
 * @param {number} options.limit - Max number of entries
 * @param {string} options.rating - Filter by rating
 * @param {number} options.days - Filter by days back
 * @returns {Promise<Object>} Feedback logs and stats
 */
export const getFeedbackLogs = async (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.rating) params.append('rating', options.rating);
    if (options.days) params.append('days', options.days);

    const url = `${config.backendApiUrl}/feedback${params.toString() ? '?' + params.toString() : ''}`;
    const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch feedback: ${response.status}`);
    }

    return response.json();
};
